import React, { useState } from 'react';
import type { Decision } from '../../../lib/aiJobSummary';
import { Badge, Button, Icon } from '../../ui';
import { DecisionWhy } from './NeedToKnow';

/**
 * Approve to Build, when the spec summary carries decisions. The operator
 * answers them here and the answers ride into the build queue with the job
 * (operator_summary.spec.decisions[].answer), where `cockpit ai get` prints
 * them for the build agent. Custom overlay; no native confirm().
 */
export default function DecisionsDialog({
  decisions,
  busy,
  onCancel,
  onConfirm,
}: {
  decisions: Decision[];
  busy: boolean;
  onCancel: () => void;
  onConfirm: (answers: Record<string, string>) => void;
}) {
  const [answers, setAnswers] = useState<Record<string, string>>(() =>
    Object.fromEntries(decisions.filter((d) => d.answer).map((d) => [d.id, d.answer as string]))
  );
  const [custom, setCustom] = useState<Record<string, string>>({});
  const unanswered = decisions.filter((d) => !answers[d.id]).length;

  const pick = (id: string, value: string) => {
    setAnswers((prev) => {
      const next = { ...prev };
      if (next[id] === value) delete next[id];
      else next[id] = value;
      return next;
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Decisions before build"
      // Phones: the overlay runs under the notch and the bottom nav, so the card keeps clear of both.
      className="px-[12px] pb-[calc(var(--bottom-nav-h)+var(--sab)+12px)] pt-[calc(var(--sat)+12px)] md:p-[24px]"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.45)',
        backdropFilter: 'blur(2px)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div
        className="ck-stagger px-[16px] pb-[16px] pt-[16px] md:px-[22px] md:pb-[18px] md:pt-[20px]"
        style={{
          width: '100%',
          maxWidth: '620px',
          maxHeight: '100%',
          overflowY: 'auto',
          background: 'var(--surface-card)',
          border: '1px solid var(--border-hairline)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-pop)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '30px',
              height: '30px',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--accent-soft)',
              color: 'var(--accent-text)',
              flexShrink: 0,
            }}
          >
            <Icon name="help-circle" size={16} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="t-eyebrow" style={{ marginBottom: '3px' }}>
              Before the build
            </div>
            <div style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'], letterSpacing: 'var(--tracking-tight)', color: 'var(--text-body)' }}>
              {decisions.length === 1 ? 'One call is yours' : `${decisions.length} calls are yours`}
            </div>
          </div>
        </div>

        <p style={{ margin: 0, fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-normal)', color: 'var(--text-muted)' }}>
          The build agent reads these answers when it picks the job up. Leave one blank and it follows the recommendation.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {decisions.map((d) => {
            const options = d.options ?? [];
            const chosen = answers[d.id];
            const isCustom = chosen !== undefined && !options.includes(chosen);
            return (
              <div key={d.id} style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px 14px', borderRadius: 'var(--radius-md)', background: 'var(--surface-raised)', border: '1px solid var(--border-hairline)' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--accent-text)', paddingTop: '3px', flexShrink: 0 }}>
                    {d.id}
                  </span>
                  <span style={{ fontSize: 'var(--text-base)', lineHeight: 'var(--leading-normal)', color: 'var(--text-body)' }}>{d.question}</span>
                </div>
                <div className="pl-0 md:pl-[30px]" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                  {options.map((opt) => {
                    const selected = chosen === opt;
                    const recommended = d.recommendation === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        className={`ck-op-option pressable max-md:h-[36px]${selected ? ' is-selected' : ''}`}
                        disabled={busy}
                        aria-pressed={selected}
                        onClick={() => pick(d.id, opt)}
                      >
                        {selected ? <Icon name="check" size={13} strokeWidth={2.25} /> : null}
                        {opt}
                        {recommended && !selected ? (
                          <span className="t-eyebrow" style={{ fontSize: '9px', color: 'var(--accent-text)', letterSpacing: '0.06em' }}>
                            rec
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                  <input
                    className="ck-textarea h-[36px] md:h-[28px]"
                    style={{ minHeight: 0, padding: '0 10px', flex: '1 1 160px', fontSize: 'var(--text-sm)', resize: 'none' }}
                    placeholder="or say it your way"
                    value={isCustom ? chosen : custom[d.id] ?? ''}
                    disabled={busy}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCustom((prev) => ({ ...prev, [d.id]: v }));
                      setAnswers((prev) => {
                        const next = { ...prev };
                        if (v.trim()) next[d.id] = v;
                        else if (isCustom) delete next[d.id];
                        return next;
                      });
                    }}
                  />
                </div>
                <DecisionWhy decision={d} />
              </div>
            );
          })}
        </div>

        {/* Phones: the questions scroll and Back / Approve stay pinned to the card's bottom edge. */}
        <div
          className="max-md:sticky max-md:bottom-[-16px] max-md:-mx-[16px] max-md:border-t max-md:border-[var(--border-hairline)] max-md:bg-[var(--surface-card)] max-md:px-[16px] max-md:py-[10px]"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}
        >
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
            {unanswered === 0 ? (
              <Badge tone="up" dot>
                all answered
              </Badge>
            ) : (
              `${unanswered} unanswered · builder uses the recommendation`
            )}
          </span>
          <div className="w-full justify-end md:w-auto" style={{ display: 'flex', gap: '8px' }}>
            <Button variant="ghost" disabled={busy} onClick={onCancel}>
              Back
            </Button>
            <Button icon="check" disabled={busy} onClick={() => onConfirm(answers)}>
              {busy ? 'Approving…' : 'Approve to Build'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
